import {useCallback, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import toColumnWidth from 'notebook/structured-tables/columns/toColumnWidth';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import type {StructuredTableUpdateHandler} from 'notebook/structured-tables/StructuredTableOutsideNotebook';

/**
 * Storage for user-adjusted column widths, keyed by column `provenanceUuid`.
 *
 * Supply this to persist widths yourself (e.g. to localStorage or a backend) so they survive across
 * mounts. Omit it to let {@link useStructuredTableColumnWidths} hold the widths in local component
 * state: they stay adjustable while the table is visible but are discarded on unmount.
 */
export interface StructuredTableColumnWidthStore {
  /** Explicit widths keyed by column `provenanceUuid`. Columns absent here keep their own `width`. */
  widthsByColumnId: Readonly<Record<string, number>>;
  setWidthsByColumnId: (widthsByColumnId: Record<string, number>) => void;
}

function resolvedWidthsByColumnId(structuredTable: UnknownStructuredTable): Record<string, number> {
  const widths: Record<string, number> = {};
  for (const column of structuredTable.columns) {
    widths[column.provenanceUuid] = toColumnWidth(column);
  }
  return widths;
}

function orderedColumnIds(structuredTable: UnknownStructuredTable): Array<string> {
  return structuredTable.columns.map((column) => column.provenanceUuid);
}

function haveSameColumns(a: Array<string>, b: Array<string>): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

function applyStoredWidths(
  structuredTable: UnknownStructuredTable,
  storedWidths: Readonly<Record<string, number>>
): UnknownStructuredTable {
  const columns = structuredTable.columns.map((column) => {
    const storedWidth = storedWidths[column.provenanceUuid];
    return storedWidth != null && storedWidth !== column.width ? {...column, width: storedWidth} : column;
  });
  const changed = columns.some((column, index) => column !== structuredTable.columns[index]);
  return changed ? {...structuredTable, columns} : structuredTable;
}

/**
 * Undoes this hook's width overlay before a table is handed back to the caller.
 *
 * Columns are rendered with stored widths overlaid onto `column.width`, so a non-resize edit (cell
 * change, rename, add or remove column) round-trips through the overlaid table and its `updated`
 * columns carry those overlaid widths. Forwarding them unchanged would let the caller persist widths
 * the store owns, so restore each existing column's width to the caller's own input value. A column
 * the edit added has no input value, so keep its width as-is.
 */
function restoreOriginalColumnWidths(
  updated: UnknownStructuredTable,
  original: UnknownStructuredTable
): UnknownStructuredTable {
  const originalWidthByColumnId = new Map(
    original.columns.map((column) => [column.provenanceUuid, column.width])
  );
  const columns = updated.columns.map((column) => {
    const originalWidth = originalWidthByColumnId.get(column.provenanceUuid);
    if (originalWidth == null || column.width === originalWidth) {
      return column;
    }
    return {...column, width: originalWidth};
  });
  const changed = columns.some((column, index) => column !== updated.columns[index]);
  return changed ? {...updated, columns} : updated;
}

/**
 * Makes {@link StructuredTableOutsideNotebook} column widths adjustable independently of whether the
 * caller persists the structured table.
 *
 * STON has no width persistence of its own: a resize round-trips through `updateHandler`, so widths
 * only stick if the caller writes the returned table back and re-renders. This hook adds a width
 * store between the two — it overlays stored widths onto the columns before rendering and diverts
 * resize-only updates to the store, forwarding everything else (cell edits, renames, adding or
 * removing columns) to the caller's `updateHandler` unchanged.
 *
 * Spread the returned `structuredTable`/`updateHandler` into STON and set `enableColumnResizing`.
 */
export default function useStructuredTableColumnWidths({
  structuredTable,
  updateHandler,
  store,
}: {
  structuredTable: UnknownStructuredTable;
  updateHandler: StructuredTableUpdateHandler;
  store?: StructuredTableColumnWidthStore;
}): {structuredTable: UnknownStructuredTable; updateHandler: StructuredTableUpdateHandler} {
  // The in-memory fallback is keyed by column provenanceUuid, which is not globally unique and is
  // reused across table families (e.g. procedure config tables). If this same mounted component later
  // renders a different table, drop the old widths so they can't bleed onto a new table's
  // identically-identified columns. A caller-supplied store owns its own lifetime, so leave it alone.
  const [localState, setLocalState] = useState<{tableId: string; widths: Record<string, number>}>(() => ({
    tableId: structuredTable.cid,
    widths: {},
  }));
  let localWidths = localState.widths;
  if (localState.tableId !== structuredTable.cid) {
    localWidths = {};
    setLocalState({tableId: structuredTable.cid, widths: localWidths});
  }
  const setLocalWidths = useCallback((widths: Record<string, number>) => {
    setLocalState((prev) => ({tableId: prev.tableId, widths}));
  }, []);

  const storedWidths = store?.widthsByColumnId ?? localWidths;
  const setStoredWidths = store?.setWidthsByColumnId ?? setLocalWidths;

  const displayedStructuredTable = useMemo(
    () => applyStoredWidths(structuredTable, storedWidths),
    [structuredTable, storedWidths]
  );

  const wrappedUpdateHandler = useCallback<StructuredTableUpdateHandler>(
    (updated, overrideArchivePurposeByRow) => {
      const previousWidths = resolvedWidthsByColumnId(displayedStructuredTable);
      const updatedWidths = resolvedWidthsByColumnId(updated);
      const columnsUnchanged = haveSameColumns(
        orderedColumnIds(displayedStructuredTable),
        orderedColumnIds(updated)
      );
      const resizedColumnIds = Object.keys(updatedWidths).filter(
        (id) => updatedWidths[id] !== previousWidths[id]
      );

      // Resizing is the only edit that changes column widths without also adding, removing, or
      // reordering columns. Send it to the width store and leave the caller's update handler alone,
      // so widths stay adjustable even when the caller doesn't persist the structured table.
      const restored = restoreOriginalColumnWidths(updated, structuredTable);
      if (columnsUnchanged && resizedColumnIds.length > 0) {
        const nextWidths = {...storedWidths};
        for (const id of resizedColumnIds) {
          nextWidths[id] = updatedWidths[id];
        }
        setStoredWidths(nextWidths);
        // A pure resize differs from the caller's table only in widths, so `restored` matches it and
        // there is nothing else to forward. If the same update also changed a cell, row, or column
        // property, `restored` still carries that change, so forward it too: the width delta goes to
        // the store while the domain edit still reaches the caller, never silently dropped.
        if (!isEqual(restored, structuredTable)) {
          updateHandler(restored, overrideArchivePurposeByRow);
        }
        return;
      }

      updateHandler(restored, overrideArchivePurposeByRow);
    },
    [structuredTable, displayedStructuredTable, storedWidths, setStoredWidths, updateHandler]
  );

  return {structuredTable: displayedStructuredTable, updateHandler: wrappedUpdateHandler};
}
